import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { wizardConfig, calculateIAMScore, getMaturityLevel } from '../config/wizardConfig';
import StepQuestion from './wizard/StepQuestion';
import StepAnalysis from './wizard/StepAnalysis';
import StepResults from './wizard/StepResults';

export default function DiagnosticWizard({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const steps = wizardConfig.steps;
  const totalSteps = steps.length;

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setAnswers({});
      setIsAnalyzing(false);
      setShowResults(false);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      setIsAnalyzing(true);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleAnswerChange = (stepId, value) => {
    setAnswers(prev => ({ ...prev, [stepId]: value }));
  };

  const handleAnalysisComplete = () => {
    setIsAnalyzing(false);
    setShowResults(true);
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  const currentStepConfig = steps[currentStep];
  const currentQuestions = wizardConfig.questions[currentStepConfig?.id] || [];
  const currentAnswers = answers[currentStepConfig?.id] || {};

  // Check if current step has at least one answer
  const hasAnswer = currentQuestions.some(q => {
    const a = currentAnswers[q.id];
    if (q.type === 'checkbox') return Array.isArray(a) && a.length > 0;
    return a !== undefined && a !== null && a !== '';
  });

  const iamScore = calculateIAMScore(answers);
  const maturity = getMaturityLevel(iamScore.total);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background/95 backdrop-blur-md">
      <button
        onClick={handleClose}
        className="fixed top-6 right-6 text-muted-foreground hover:text-foreground transition-colors z-10"
      >
        <X className="w-8 h-8" />
      </button>

      <div className="w-full max-w-3xl mx-auto px-4 py-12">
        {isAnalyzing ? (
          <StepAnalysis onComplete={handleAnalysisComplete} />
        ) : showResults ? (
          <StepResults
            iamScore={iamScore}
            maturity={maturity}
            answers={answers}
            onClose={handleClose}
          />
        ) : (
          <>
            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-2xl mr-2">{currentStepConfig.icon}</span>
                  <span className="text-xl font-bold">{currentStepConfig.title}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  Étape {currentStep + 1} / {totalSteps}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{currentStepConfig.subtitle}</p>
              <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
                  style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Questions */}
            <div className="glass rounded-2xl p-8 mb-8">
              <StepQuestion
                questions={currentQuestions}
                answers={currentAnswers}
                onChange={(val) => handleAnswerChange(currentStepConfig.id, val)}
              />
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="border-border hover:bg-muted/50"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Précédent
              </Button>

              <Button
                onClick={handleNext}
                disabled={!hasAnswer}
                className="bg-primary hover:bg-primary/90 text-primary-foreground glow-purple disabled:opacity-40"
              >
                {currentStep === totalSteps - 1 ? 'Calculer mon score' : 'Suivant'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
